import {
	DEFAULT_UPLOAD_CHUNK_SIZE,
	MAX_CHUNKED_UPLOAD_FILE_SIZE,
	MAX_JSON_UPLOAD_CHUNK_SIZE,
} from "@repo/api/src/schemas/upload-limits";
import { useCallback, useRef, useState } from "react";
import { useUploadDropBusy } from "../components/upload/GlobalUploadDrop";

export interface ChunkedUploadProgress {
	uploadId: string;
	uploadedChunks: number;
	totalChunks: number;
	percentage: number;
	isComplete: boolean;
	uploadedSizeMB: number;
	totalSizeMB: number;
	uploadSpeedMBps: number;
	remainingTimeSeconds: number;
	stage: "preparing" | "sending" | "waiting" | "finalizing" | "link";
}
export type UploadFailureCategory =
	| "offline"
	| "network"
	| "timeout"
	| "server"
	| "busy"
	| "file"
	| "permission"
	| "unknown";
export function uploadFailureCategory(status?: number): UploadFailureCategory {
	if (status === 401 || status === 403) return "permission";
	if (status === 429) return "busy";
	if (status && status >= 500) return "server";
	if (status === 408) return "timeout";
	if (status && [400, 413, 415, 422].includes(status)) return "file";
	return "unknown";
}
class UploadFailure extends Error {
	constructor(
		public category: UploadFailureCategory,
		message: string,
		public status?: number,
	) {
		super(message);
	}
}
interface UploadResult {
	success: boolean;
	fileUrl?: string;
	filename?: string;
	error?: string;
	category?: UploadFailureCategory;
	status?: number;
	uncertain?: boolean;
	cancelled?: boolean;
}
const MB = 1024 * 1024;

export function useChunkedUpload() {
	const [progress, setProgress] = useState<ChunkedUploadProgress | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	useUploadDropBusy(isUploading);
	const controllerRef = useRef<AbortController | null>(null);
	const cancelUpload = useCallback(() => controllerRef.current?.abort(), []);
	const reset = useCallback(() => setProgress(null), []);
	const setStage = useCallback(
		(stage: ChunkedUploadProgress["stage"]) => setProgress(p => (p ? { ...p, stage } : p)),
		[],
	);
	const uploadFile = useCallback(
		async (
			file: File,
			metadata?: Record<string, unknown>,
			editor?: { id: string; kind: "model" | "json" | "bin" | "lod"; bundleId?: string },
		): Promise<UploadResult> => {
			const controller = new AbortController();
			controllerRef.current = controller;
			const { signal } = controller;
			let finalizing = false;
			const checkCancelled = () => {
				if (signal.aborted) throw new DOMException("Upload cancelled", "AbortError");
			};
			// Use the same authenticated tRPC endpoint as the application, without batching or a transformer.
			const request = <T,>(
				procedure: string,
				input: unknown,
				onProgress?: (fraction: number) => void,
				binary?: { url: string; body: Blob },
			): Promise<T> =>
				new Promise((resolve, reject) => {
					const xhr = new XMLHttpRequest();
					const abort = () => xhr.abort();
					const cleanup = () => signal.removeEventListener("abort", abort);
					xhr.open(
						"POST",
						binary?.url ??
							`${import.meta.env.VITE_TRPC_URL || "/share/trpc"}/${editor ? "library" : "order"}.${procedure}`,
					);
					xhr.withCredentials = true;
					xhr.timeout = 10 * 60 * 1000;
					xhr.ontimeout = () => {
						cleanup();
						reject(new UploadFailure("timeout", "Upload request timed out"));
					};
					xhr.setRequestHeader("Content-Type", binary ? "application/octet-stream" : "application/json");
					xhr.upload.onprogress = event => {
						if (event.lengthComputable) onProgress?.(event.loaded / event.total);
					};
					xhr.onload = () => {
						cleanup();
						try {
							if (xhr.status < 200 || xhr.status >= 300)
								throw new UploadFailure(uploadFailureCategory(xhr.status), `HTTP ${xhr.status}`, xhr.status);
							let response: { result: { data: T }; error?: { message?: string; data?: { httpStatus?: number } } };
							try {
								response = JSON.parse(xhr.responseText);
							} catch {
								throw new UploadFailure("server", "Invalid server response", xhr.status);
							}
							if (xhr.status < 200 || xhr.status >= 300 || response.error)
								throw new UploadFailure(
									uploadFailureCategory(response.error?.data?.httpStatus),
									response.error?.message || `HTTP ${xhr.status}`,
									response.error?.data?.httpStatus || xhr.status,
								);
							resolve(response.result.data as T);
						} catch (error) {
							reject(error);
						}
					};
					xhr.onerror = () => {
						cleanup();
						reject(new UploadFailure(navigator.onLine ? "network" : "offline", "Network error"));
					};
					xhr.onabort = () => {
						cleanup();
						reject(new DOMException("Upload cancelled", "AbortError"));
					};
					signal.addEventListener("abort", abort, { once: true });
					if (signal.aborted) {
						cleanup();
						reject(new DOMException("Upload cancelled", "AbortError"));
						return;
					}
					xhr.send(binary?.body ?? JSON.stringify(input));
				});
			setIsUploading(true);
			setProgress({
				uploadId: "",
				uploadedChunks: 0,
				totalChunks: Math.ceil(file.size / DEFAULT_UPLOAD_CHUNK_SIZE),
				percentage: 0,
				isComplete: false,
				uploadedSizeMB: 0,
				totalSizeMB: file.size / MB,
				uploadSpeedMBps: 0,
				remainingTimeSeconds: 0,
				stage: "preparing",
			});
			try {
				if (!file.size || file.size > MAX_CHUNKED_UPLOAD_FILE_SIZE)
					throw new UploadFailure("file", "Invalid file size");
				const {
					uploadId,
					totalChunks,
					chunkSize = DEFAULT_UPLOAD_CHUNK_SIZE,
				} = await request<{ uploadId: string; totalChunks: number; chunkSize?: number }>("chunkedUploadInitiate", {
					filename: file.name,
					fileSize: file.size,
					chunkSize: DEFAULT_UPLOAD_CHUNK_SIZE,
					contentType: file.type,
					metadata,
					...(editor ?? {}),
				});
				checkCancelled();
				const started = performance.now();
				const update = (bytes: number, chunks: number) => {
					const seconds = (performance.now() - started) / 1000;
					const speed = seconds > 0 ? bytes / MB / seconds : 0;
					setProgress({
						uploadId,
						totalChunks,
						uploadedChunks: chunks,
						percentage: Math.min(100, Math.floor((bytes / file.size) * 100)),
						isComplete: false,
						uploadedSizeMB: bytes / MB,
						totalSizeMB: file.size / MB,
						uploadSpeedMBps: speed,
						remainingTimeSeconds: speed > 0 ? Math.ceil((file.size - bytes) / MB / speed) : 0,
						stage: "sending",
					});
				};
				for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
					checkCancelled();
					const start = chunkIndex * chunkSize;
					const chunk = file.slice(start, Math.min(start + chunkSize, file.size));
					const binary = chunk.size > MAX_JSON_UPLOAD_CHUNK_SIZE;
					const data = binary
						? undefined
						: await new Promise<string>((resolve, reject) => {
								const reader = new FileReader();
								const abort = () => reader.abort();
								signal.addEventListener("abort", abort, { once: true });
								reader.onload = () => {
									signal.removeEventListener("abort", abort);
									resolve(String(reader.result).split(",")[1]);
								};
								reader.onerror = () => {
									signal.removeEventListener("abort", abort);
									reject(new UploadFailure("file", "Failed to read file"));
								};
								reader.onabort = () => {
									signal.removeEventListener("abort", abort);
									reject(new DOMException("Upload cancelled", "AbortError"));
								};
								reader.readAsDataURL(chunk);
							});
					checkCancelled();
					update(start, chunkIndex);
					// Browser-reported transmitted request bytes mapped to the corresponding file payload.
					// 100% means transmitted; finalizing is a separate, unestimated server phase.
					await request(
						"chunkedUploadChunk",
						{ uploadId, chunkIndex, totalChunks, data, size: chunk.size },
						fraction => {
							update(start + chunk.size * fraction, chunkIndex);
							if (fraction >= 1) setStage("waiting");
						},
						binary
							? {
									url: `${(import.meta.env.VITE_TRPC_URL || "/share/trpc").replace(/\/trpc\/?$/, "")}/upload/chunk/${encodeURIComponent(uploadId)}/${chunkIndex}?totalChunks=${totalChunks}&size=${chunk.size}`,
									body: chunk,
								}
							: undefined,
					);
					update(start + chunk.size, chunkIndex + 1);
				}
				checkCancelled();
				finalizing = true;
				setStage("finalizing");
				const result = await request<UploadResult>("chunkedUploadFinalize", {
					uploadId,
					totalChunks,
					filename: file.name,
					metadata,
				});
				checkCancelled();
				setProgress(p => (p ? { ...p, isComplete: true, stage: "link" } : p));
				return result;
			} catch (error) {
				return {
					success: false,
					cancelled: signal.aborted,
					uncertain: finalizing && (!(error instanceof UploadFailure) || !error.status || error.status >= 500),
					error: error instanceof Error ? error.message : String(error),
					category: error instanceof UploadFailure ? error.category : "unknown",
					status: error instanceof UploadFailure ? error.status : undefined,
				};
			} finally {
				controllerRef.current = null;
				setIsUploading(false);
			}
		},
		[setStage],
	);
	return { uploadFile, progress, isUploading, cancelUpload, reset, setStage };
}
